import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InMemoryCache, useApolloClient, useQuery } from "@apollo/client";
import {
  FindMessageThreadsQuery,
  FindMessageThreadsQueryVariables,
  MessageThreadSortType,
  SearchFilter,
} from "gql/graphql";
import FIND_MESSAGE_THREADS_QUERY from "modules/messaging/_networking/find-message-threads";
import { useAuth } from "modules/providers/auth-provider";
import { DEFAULT_PAGE_SIZE } from "modules/utils/Constants";
import { messageThreadFindQueryRegistry } from "app/message-thread-find-registry";

interface IProps {
  searchFilters: SearchFilter[];
  sortType?: MessageThreadSortType;
  skip: boolean;
  limit?: boolean;
}

const useMessageThreadFind = ({
  searchFilters,
  sortType = MessageThreadSortType.SortTime,
  skip,
  limit = true,
}: IProps) => {
  const { isLoggedIn } = useAuth();
  const [canLoadMore, setCanLoadMore] = useState(false);
  const client = useApolloClient();
  const cache = client.cache as InMemoryCache;
  const isFirstLoad = useRef(true);

  const findMessageThreadsQueryVariables = useMemo(
    (): FindMessageThreadsQueryVariables => ({
      input: {
        pageSize: limit ? DEFAULT_PAGE_SIZE : 10000,
        searchFilterList: searchFilters,
        sortType,
      },
    }),
    [limit, searchFilters, sortType],
  );

  useEffect(() => {
    messageThreadFindQueryRegistry.register(
      cache.policies.getStoreFieldName({
        typename: "Query",
        fieldName: "find",
        args: { input: findMessageThreadsQueryVariables.input },
      }),
      findMessageThreadsQueryVariables,
    );
    return () => {
      messageThreadFindQueryRegistry.unregister(
        findMessageThreadsQueryVariables,
      );
    };
  }, [cache.policies, findMessageThreadsQueryVariables]);

  const { loading, data, error, fetchMore, refetch } = useQuery<
    FindMessageThreadsQuery,
    FindMessageThreadsQueryVariables
  >(FIND_MESSAGE_THREADS_QUERY, {
    variables: findMessageThreadsQueryVariables,
    skip: !isLoggedIn || skip,
  });

  useEffect(() => {
    if (loading || !data) return;
    if (isFirstLoad.current) {
      setCanLoadMore(data.find.length === DEFAULT_PAGE_SIZE);
      isFirstLoad.current = false;
      return;
    }
    // a refetch (e.g. the websocket reconnect refetches every observable
    // query) replaces the list with a single page; paging must be possible
    // again even if the last "load more" had reached the end
    if (data.find.length === DEFAULT_PAGE_SIZE) {
      setCanLoadMore(true);
    }
  }, [data, loading]);

  const cursor = useMemo(() => {
    if (!data || !data.find || data.find.length === 0) return undefined;
    const lastMessageThread = data.find[data.find.length - 1];
    switch (sortType) {
      case MessageThreadSortType.SortTime: {
        return lastMessageThread.sortKeys?.defaultSortKey;
      }
      case MessageThreadSortType.TaskDueTime: {
        return lastMessageThread.sortKeys?.taskDueSortKey;
      }
      case MessageThreadSortType.TaskCompleteTime: {
        return lastMessageThread.sortKeys?.taskCompleteSortKey;
      }
      default: {
        return lastMessageThread.sortKeys?.defaultSortKey;
      }
    }
  }, [data, sortType]);

  // Virtuoso re-fires `endReached` every time the list data changes while its
  // last row is visible, and websocket-driven cache updates change the list
  // constantly. Without this guard a burst of updates (e.g. after waking from
  // sleep) issues the same page request several times and each response is
  // appended, duplicating rows (SYN-12).
  const isFetchingMore = useRef(false);

  const _fetchMore = useCallback(() => {
    if (isFetchingMore.current) return;
    isFetchingMore.current = true;

    fetchMore({
      variables: {
        input: {
          searchFilterList: searchFilters,
          cursor,
          pageSize: DEFAULT_PAGE_SIZE,
          sortType,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        setCanLoadMore(fetchMoreResult.find.length === DEFAULT_PAGE_SIZE);
        // a thread can be in both lists when a notification moved it into
        // the already-loaded rows while this page was in flight
        const existing = prev?.find ?? [];
        const existingIds = new Set(existing.map((thread) => thread.id));
        return {
          find: [
            ...existing,
            ...fetchMoreResult.find.filter(
              (thread) => !existingIds.has(thread.id),
            ),
          ],
        };
      },
    }).finally(() => {
      isFetchingMore.current = false;
    });
  }, [fetchMore, searchFilters, cursor, sortType]);

  const messageThreads = useMemo(() => data?.find ?? [], [data?.find]);

  return {
    messageThreads,
    canLoadMore,
    loading,
    error,
    fetchMore: _fetchMore,
    refetch,
  };
};

export default useMessageThreadFind;
